import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormInput,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { SendIcon } from "lucide-react";
import { z } from "zod";
import { useWhatsAppWorkerTestSend } from "../api/queries";

const TestSendSchema = z.object({
  phone: z
    .string()
    .trim()
    .min(8, "ເບີຕ້ອງມີຢ່າງໜ້ອຍ 8 ຕົວເລກ")
    .max(20),
  message: z.string().trim().min(1, "ກະລຸນາໃສ່ຂໍ້ຄວາມ").max(2000),
});

type TestSendValues = z.infer<typeof TestSendSchema>;

export function WorkerTestSendDialog({
  open,
  onOpenChange,
  onSuccess,
  onError,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  onError: (message: string) => void;
}) {
  const testSend = useWhatsAppWorkerTestSend();

  const methods = RHF.useForm<TestSendValues>({
    resolver: zodResolver(TestSendSchema),
    defaultValues: {
      phone: "",
      message: "ທົດສອບຈາກ Fanglao Admin",
    },
  });

  const onSubmit = async (values: TestSendValues) => {
    try {
      const result = await testSend.mutateAsync(values);
      if (!result.ok) {
        onError(result.error ?? "ສົ່ງບໍ່ສຳເລັດ");
        return;
      }
      onSuccess();
      onOpenChange(false);
      methods.reset({
        phone: values.phone,
        message: "ທົດສອບຈາກ Fanglao Admin",
      });
    } catch {
      onError("ສົ່ງບໍ່ສຳເລັດ — ກວດ Worker ແລະການເຊື່ອມຕໍ່");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>ທົດສອບສົ່ງ WhatsApp</DialogTitle>
          <DialogDescription>
            ສົ່ງຂໍ້ຄວາມທົດສອບໂດຍກົງຜ່ານ Worker (ບໍ່ຜ່ານຄິວແຈ້ງເຕືອນ)
          </DialogDescription>
        </DialogHeader>
        <FormRoot methods={methods} onSubmit={onSubmit} className="space-y-4">
          <FormInput
            name="phone"
            label="ເບີໂທ"
            placeholder="96619695"
            autoComplete="tel"
          />
          <p className="-mt-2 text-muted-foreground text-xs">
            ຕົວຢ່າງ: 96619695, 02096619695, ຫຼື 8562096619695 (ຕ້ອງມີ WhatsApp)
          </p>
          <FormTextarea
            name="message"
            label="ຂໍ້ຄວາມ"
            rows={4}
            placeholder="ຂໍ້ຄວາມທົດສອບ"
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={testSend.isPending}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={testSend.isPending}>
              <SendIcon className="mr-1 size-4" />
              {testSend.isPending ? "ກຳລັງສົ່ງ..." : "ສົ່ງທົດສອບ"}
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
